/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

export const JURISDICTIONS = {
  gdpr: {
    value: "gdpr",
    label: "European Union (GDPR)",
    text: "As an EU resident, I am exercising my right to erasure ('right to be forgotten') under Article 17 of the GDPR.",
  },
  uk_gdpr: {
    value: "uk_gdpr",
    label: "United Kingdom (UK GDPR)",
    text: "As a UK resident, I am exercising my right to erasure under Article 17 of the UK GDPR.",
  },
  ccpa: {
    value: "ccpa",
    label: "California, USA (CCPA/CPRA)",
    text: "As a California resident, I am exercising my right to delete personal information under Section 1798.105 of the CCPA, as amended by the CPRA.",
  },
  pipeda: {
    value: "pipeda",
    label: "Canada (PIPEDA)",
    text: "As a Canadian resident, I am requesting the deletion of my personal information under PIPEDA Principle 4.3.8.",
  },
  lgpd: {
    value: "lgpd",
    label: "Brazil (LGPD)",
    text: "As a Brazilian resident, I am exercising my right to deletion of personal data under Article 18 of the LGPD.",
  },
  pdpa_singapore: {
    value: "pdpa_singapore",
    label: "Singapore (PDPA)",
    text: "As a Singapore resident, I am requesting the deletion of my personal data under Section 16 of the PDPA.",
  },
  pipl: {
    value: "pipl",
    label: "China (PIPL)",
    text: "As a Chinese resident, I am exercising my right to request deletion of personal information under Article 47 of the PIPL.",
  },
  appi: {
    value: "appi",
    label: "Japan (APPI)",
    text: "As a Japanese resident, I am exercising my right to request deletion of personal information under Article 30 of the amended APPI.",
  },
  privacy_act_australia: {
    value: "privacy_act_australia",
    label: "Australia (Privacy Act)",
    text: "As an Australian resident, I am requesting the deletion of my personal information under the Australian Privacy Principles.",
  },
  popia: {
    value: "popia",
    label: "South Africa (POPIA)",
    text: "As a South African resident, I am exercising my right to request deletion of personal information under Section 24 of POPIA.",
  },
  pdpl_turkey: {
    value: "pdpl_turkey",
    label: "Turkey (PDPL)",
    text: "As a Turkish resident, I am exercising my right to request deletion of personal data under Article 7 of the PDPL.",
  },
  other: {
    value: "other",
    label: "Other Jurisdiction",
    text: "I am requesting the deletion of my personal data under applicable privacy laws in my jurisdiction.",
  },
} as const;

export const CHANNEL_TYPE_LABELS: Record<string, string> = {
  GUILD_TEXT: "Text Channel",
  DM: "Direct Message",
  GROUP_DM: "Group DM",
  GUILD_VOICE: "Voice Channel",
  GUILD_ANNOUNCEMENT: "Announcement Channel",
  ANNOUNCEMENT_THREAD: "Announcement Thread",
  PUBLIC_THREAD: "Public Thread",
  PRIVATE_THREAD: "Private Thread",
  GUILD_STAGE_VOICE: "Stage Channel",
  GUILD_DIRECTORY: "Directory",
  GUILD_FORUM: "Forum Channel",
  GUILD_MEDIA: "Media Channel",
  UNKNOWN: "Unknown Channel Type",
};

export function getChannelTypeLabel(type: string): string {
  return CHANNEL_TYPE_LABELS[type] || CHANNEL_TYPE_LABELS.UNKNOWN;
}

export const COUNTER_RESPONSE_TEMPLATE = `Dear Privacy Team,

I believe there may have been a misunderstanding regarding my bulk message deletion request submitted through the official privacy form. This is a legitimate privacy request submitted via the correct channel.

I submitted this request under "Delete your personal data on Discord" and included a properly formatted CSV file containing the message IDs I wish to have deleted. I understand that Discord will only process deletions for messages in servers and group DMs that I have already left, and I have ensured this requirement is met.

For reference, previous tickets like #50057053 demonstrate the standard process for handling such requests. Since December 1, 2024, Discord's privacy team accepts CSV files for these requests, which I have provided in the correct format.

Could you please review my request again and process it according to Discord's established privacy deletion procedures?

Thank you for your time and assistance.

Best regards,
[Your Name]`;
